import * as React from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod' 
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Search, ChevronsUpDown, Check, X, Undo2, Loader2 } from "lucide-react";
import { getCategoryLabel, AI_MODEL_CATEGORIES, AI_NODE_TYPES, AI_MODEL_PURPOSE } from "@genieartinc/schemas";

interface AiModel {
  id: string;
  name: string;
  endpointId: string;
  description?: string;
  providerId: string;
  providerCategory: string;
  nodeType: string;
  purpose?: string;
  modelFamily?: string;
  enabled: boolean;
  featuredScore: number;
  priceUsd: number | null;
  adminDisabled: boolean;
  adminChecked: boolean;
}

interface FilterOptions {
  modelFamilies: string[];
  providerCategories: string[];
  nodeTypes: string[];
  purposes: string[];
  providers: { id: string; name: string }[];
}

const aiModelsSearchSchema = z.object({
  search: z.string().optional(),
  providerId: z.string().optional(),
  providerCategory: z.string().optional(),
  nodeType: z.string().optional(),
  modelFamily: z.string().optional(),
  purpose: z.string().optional(),
  enabled: z.string().optional(),
  limit: z.coerce.number().optional(),
  page: z.coerce.number().optional(),
})

export const Route = createFileRoute('/dashboard/model-management/')({
  validateSearch: aiModelsSearchSchema,
  component: AiModelsPage,
})

function AiModelsPage() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const [models, setModels] = React.useState<AiModel[]>([]);
  const [filterOptions, setFilterOptions] = React.useState<FilterOptions>({ modelFamilies: [], providerCategories: [], nodeTypes: [], purposes: [], providers: [] });
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [selectedModels, setSelectedModels] = React.useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = React.useState(false);

  // Inline editing state: modelId -> { category?, nodeType?, purpose?, priceUsd? }
  const [rowEdits, setRowEdits] = React.useState<Record<string, { providerCategory?: string; nodeType?: string; purpose?: string; priceUsd?: number | string }>>({});
  const [savingRows, setSavingRows] = React.useState<Set<string>>(new Set());
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  const [togglingEnabled, setTogglingEnabled] = React.useState<Set<string>>(new Set());

  const toggleEnabled = React.useCallback(async (modelId: string, currentEnabled: boolean) => {
    setTogglingEnabled((prev) => new Set(prev).add(modelId));
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/model-management/${encodeURIComponent(modelId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !currentEnabled }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Toggle failed");
      }
      // Optimistic update
      setModels((prev) => prev.map((m) => m.id === modelId ? { ...m, enabled: !currentEnabled } : m));
    } catch (error) {
      console.error("Toggle enabled error:", error);
      toast.error("Failed to toggle model. Please try again.");
    } finally {
      setTogglingEnabled((prev) => {
        const next = new Set(prev);
        next.delete(modelId);
        return next;
      });
    }
  }, []);

  const updateRowEdit = React.useCallback((modelId: string, field: string, value: string | number) => {
    setRowEdits((prev) => {
      const current = prev[modelId] || {};
      const model = models.find((m) => m.id === modelId);
      if (!model) return prev;

      const updated = { ...current, [field]: value };

      // Check if all edited fields match original values — if so, remove the edit
      const originalProviderCategory = model.providerCategory;
      const originalNodeType = model.nodeType;
      const originalPurpose = model.purpose || "";
      const originalPriceUsd = model.priceUsd;

      const editedProviderCategory = updated.providerCategory ?? originalProviderCategory;
      const editedNodeType = updated.nodeType ?? originalNodeType;
      const editedPurpose = updated.purpose ?? originalPurpose;
      const editedPriceUsd = updated.priceUsd !== undefined ? Number(updated.priceUsd) : originalPriceUsd;

      if (
        editedProviderCategory === originalProviderCategory &&
        editedNodeType === originalNodeType &&
        editedPurpose === originalPurpose &&
        editedPriceUsd === originalPriceUsd
      ) {
        const rest = { ...prev };
        delete rest[modelId];
        return rest;
      }

      return { ...prev, [modelId]: updated };
    });
  }, [models]);

  const discardRowEdit = React.useCallback((modelId: string) => {
    setRowEdits((prev) => {
      const rest = { ...prev };
      delete rest[modelId];
      return rest;
    });
  }, []);

  const confirmRowEdit = React.useCallback(async (modelId: string) => {
    const edits = rowEdits[modelId];
    if (!edits) return;

    setSavingRows((prev) => new Set(prev).add(modelId));
    try {
      const payload = { ...edits };
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/model-management/${encodeURIComponent(modelId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Update failed");
      }

      // Remove the edit and trigger refetch
      discardRowEdit(modelId);
      setRefetchTrigger((n) => n + 1);
    } catch (error) {
      console.error("Row update error:", error);
    } finally {
      setSavingRows((prev) => {
        const next = new Set(prev);
        next.delete(modelId);
        return next;
      });
    }
  }, [rowEdits, discardRowEdit]);

  // Search with debounce
  const [searchInput, setSearchInput] = React.useState(searchParams.search || "");
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchParams.search || "");

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Filters - Initialize from URL params (support comma-separated multi-select)
  const [selectedProviders, setSelectedProviders] = React.useState<string[]>(() => {
    const v = searchParams.providerId;
    return v ? v.split(",") : [];
  });
  const [selectedCategories, setSelectedCategories] = React.useState<string[]>(() => {
    const v = searchParams.providerCategory;
    return v ? v.split(",") : [];
  });
  const [selectedNodeTypes, setSelectedNodeTypes] = React.useState<string[]>(() => {
    const v = searchParams.nodeType;
    return v ? v.split(",") : [];
  });
  const [selectedModelFamilies, setSelectedModelFamilies] = React.useState<string[]>(() => {
    const v = searchParams.modelFamily;
    return v ? v.split(",") : [];
  });
  const [selectedPurposes, setSelectedPurposes] = React.useState<string[]>(() => {
    const v = searchParams.purpose;
    return v ? v.split(",") : [];
  });
  const [enabledFilter, setEnabledFilter] = React.useState<string>(searchParams.enabled || "all");

  // Multi-select toggle helper
  const toggleFilter = React.useCallback((current: string[], value: string): string[] => {
    return current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
  }, []);

  // Combobox open states
  const [providerOpen, setProviderOpen] = React.useState(false);
  const [nodeTypeOpen, setNodeTypeOpen] = React.useState(false);
  const [categoryOpen, setCategoryOpen] = React.useState(false);
  const [modelFamilyOpen, setModelFamilyOpen] = React.useState(false);
  const [purposeOpen, setPurposeOpen] = React.useState(false);
  const [enabledOpen, setEnabledOpen] = React.useState(false);

  // Pagination
  const [page, setPage] = React.useState(searchParams.page || 1);
  const [limit, setLimit] = React.useState(searchParams.limit || 50);


  // Update URL with current filters
  const updateURL = React.useCallback(() => {
    const params = new URLSearchParams();

    if (debouncedSearch) params.set("search", debouncedSearch);
    if (selectedProviders.length > 0) params.set("providerId", selectedProviders.join(","));
    if (selectedCategories.length > 0) params.set("providerCategory", selectedCategories.join(","));
    if (selectedNodeTypes.length > 0) params.set("nodeType", selectedNodeTypes.join(","));
    if (selectedModelFamilies.length > 0) params.set("modelFamily", selectedModelFamilies.join(","));
    if (selectedPurposes.length > 0) params.set("purpose", selectedPurposes.join(","));
    if (enabledFilter !== "all") params.set("enabled", enabledFilter);
    if (limit !== 50) params.set("limit", limit.toString());
    if (page !== 1) params.set("page", page.toString());

    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        search: debouncedSearch || undefined,
        providerId: selectedProviders.length > 0 ? selectedProviders.join(",") : undefined,
        providerCategory: selectedCategories.length > 0 ? selectedCategories.join(",") : undefined,
        nodeType: selectedNodeTypes.length > 0 ? selectedNodeTypes.join(",") : undefined,
        modelFamily: selectedModelFamilies.length > 0 ? selectedModelFamilies.join(",") : undefined,
        purpose: selectedPurposes.length > 0 ? selectedPurposes.join(",") : undefined,
        enabled: enabledFilter !== "all" ? enabledFilter : undefined,
        limit: limit !== 50 ? limit : undefined,
        page: page !== 1 ? page : undefined,
      }),
      replace: true,
    });
  }, [debouncedSearch, selectedProviders, selectedCategories, selectedNodeTypes, selectedModelFamilies, selectedPurposes, enabledFilter, limit, page, navigate]);

  const fetchModels = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());

      if (debouncedSearch) params.set("search", debouncedSearch);
      if (selectedProviders.length > 0) params.set("providerId", selectedProviders.join(","));
      if (selectedCategories.length > 0) params.set("providerCategory", selectedCategories.join(","));
      if (selectedNodeTypes.length > 0) params.set("nodeType", selectedNodeTypes.join(","));
      if (selectedModelFamilies.length > 0) params.set("modelFamily", selectedModelFamilies.join(","));
      if (selectedPurposes.length > 0) params.set("purpose", selectedPurposes.join(","));
      if (enabledFilter !== "all") params.set("enabled", enabledFilter);

      const url = `${API_ADMIN_BASE_URL}/api/admin/model-management?${params}`;

      const response = await fetch(url, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch models: ${response.status}`);
      }

      const data = await response.json();
      setModels(data.data.models);
      setTotal(data.data.total);
    } catch (error) {
      console.error("Error fetching models:", error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, selectedProviders, selectedCategories, selectedNodeTypes, selectedModelFamilies, selectedPurposes, enabledFilter, page, limit]);

  const fetchFilterOptions = React.useCallback(async () => {
    try {
      // Fetch all available filter options without cascade — multi-select needs full lists
      const url = `${API_ADMIN_BASE_URL}/api/admin/model-management/filters`;

      const response = await fetch(url, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch filter options");

      const data = await response.json();
      setFilterOptions(data.data);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  }, []);

  const bulkUpdate = React.useCallback(async (updates: { enabled?: boolean; modelFamilyId?: string; purpose?: string; featuredScore?: number }) => {
    if (selectedModels.size === 0) return;
    setBulkUpdating(true);
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/model-management/bulk-update`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: Array.from(selectedModels), updates }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Bulk update failed");
      }
      setSelectedModels(new Set());
      setRefetchTrigger((n) => n + 1);
    } catch (error) {
      console.error("Bulk update error:", error);
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedModels]);

  const toggleModel = React.useCallback((id: string) => {
    setSelectedModels((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = React.useCallback(() => {
    setSelectedModels((prev) => {
      if (prev.size === models.length && models.length > 0) {
        return new Set();
      }
      return new Set(models.map((m) => m.id));
    });
  }, [models]);

  // Clear selection and edits when page/filters change
  React.useEffect(() => {
    setSelectedModels(new Set());
    setRowEdits({});
  }, [debouncedSearch, selectedProviders, selectedCategories, selectedNodeTypes, selectedModelFamilies, selectedPurposes, enabledFilter, page]);

  // Update URL when filters change
  React.useEffect(() => {
    updateURL();
  }, [debouncedSearch, selectedProviders, selectedCategories, selectedNodeTypes, selectedModelFamilies, selectedPurposes, enabledFilter, page, updateURL]);

  // Fetch models (also re-fetches when refetchTrigger changes after row/bulk edits)
  React.useEffect(() => {
    fetchModels();
  }, [fetchModels, refetchTrigger]);

  // Set page title on mount
  React.useEffect(() => {
    document.title = "Model Management - Admin";
  }, []);

  // Fetch filter options when filters change (cascade filtering)
  React.useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Reset page to 1 when filters or limit change (not page itself)
  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedProviders, selectedCategories, selectedNodeTypes, selectedModelFamilies, selectedPurposes, enabledFilter, limit]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Model Management</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="border-border flex flex-col border-b px-4 py-4 md:px-6 md:py-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
                Model Management
              </h1>
              <p className="text-muted-foreground text-sm">
                Manage {total.toLocaleString()} AI models across all providers
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full md:w-[300px]">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Search models..."
              className="pl-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <div className="flex gap-3 flex-wrap items-center justify-between w-full">
            <div className="flex gap-3 flex-wrap">
              {/* Provider - Multi-select */}
              <Popover open={providerOpen} onOpenChange={setProviderOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={providerOpen}
                    className="w-[180px] justify-between"
                  >
                    {selectedProviders.length === 0
                      ? "All Providers"
                      : selectedProviders.length === 1
                        ? (filterOptions.providers.find(p => p.id === selectedProviders[0])?.name || selectedProviders[0])
                        : `${selectedProviders.length} providers`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-1" align="start">
                  <div className="max-h-[300px] overflow-auto space-y-0.5">
                    {selectedProviders.length > 0 && (
                      <button
                        onClick={() => setSelectedProviders([])}
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent"
                      >
                        <X className="h-4 w-4" />
                        Clear selection
                      </button>
                    )}
                    {filterOptions.providers.map((provider) => (
                      <label
                        key={provider.id}
                        className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedProviders.includes(provider.id)}
                          onCheckedChange={() => setSelectedProviders(toggleFilter(selectedProviders, provider.id))}
                        />
                        {provider.name}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Node Type - Multi-select */}
              <Popover open={nodeTypeOpen} onOpenChange={setNodeTypeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={nodeTypeOpen}
                    className="w-[160px] justify-between"
                  >
                    {selectedNodeTypes.length === 0
                      ? "All Types"
                      : selectedNodeTypes.length === 1
                        ? selectedNodeTypes[0].charAt(0).toUpperCase() + selectedNodeTypes[0].slice(1)
                        : `${selectedNodeTypes.length} types`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[200px] p-1" align="start">
                  <div className="max-h-[300px] overflow-auto space-y-0.5">
                    {selectedNodeTypes.length > 0 && (
                      <button
                        onClick={() => setSelectedNodeTypes([])}
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent"
                      >
                        <X className="h-4 w-4" />
                        Clear selection
                      </button>
                    )}
                    {filterOptions.nodeTypes.map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedNodeTypes.includes(type)}
                          onCheckedChange={() => setSelectedNodeTypes(toggleFilter(selectedNodeTypes, type))}
                        />
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

            {/* Model Family - Multi-select */}
            <Popover open={modelFamilyOpen} onOpenChange={setModelFamilyOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={modelFamilyOpen}
                  className="w-[180px] justify-between"
                >
                  {selectedModelFamilies.length === 0
                    ? "All Families"
                    : selectedModelFamilies.length === 1
                      ? selectedModelFamilies[0]
                      : `${selectedModelFamilies.length} families`}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-1">
                <div className="max-h-[300px] overflow-auto space-y-0.5">
                  {selectedModelFamilies.length > 0 && (
                    <button
                      onClick={() => setSelectedModelFamilies([])}
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent"
                    >
                      <X className="h-4 w-4" />
                      Clear selection
                    </button>
                  )}
                  {filterOptions.modelFamilies.map((family) => (
                    <label
                      key={family}
                      className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedModelFamilies.includes(family)}
                        onCheckedChange={() => setSelectedModelFamilies(toggleFilter(selectedModelFamilies, family))}
                      />
                      {family}
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Category - Multi-select */}
            <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={categoryOpen}
                  className="w-[180px] justify-between"
                >
                  {selectedCategories.length === 0
                    ? "All Categories"
                    : selectedCategories.length === 1
                      ? getCategoryLabel(selectedCategories[0])
                      : `${selectedCategories.length} categories`}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-1">
                <div className="max-h-[300px] overflow-auto space-y-0.5">
                  {selectedCategories.length > 0 && (
                    <button
                      onClick={() => setSelectedCategories([])}
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent"
                    >
                      <X className="h-4 w-4" />
                      Clear selection
                    </button>
                  )}
                  {filterOptions.providerCategories.map((category) => (
                    <label
                      key={category}
                      className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedCategories.includes(category)}
                        onCheckedChange={() => setSelectedCategories(toggleFilter(selectedCategories, category))}
                      />
                      {getCategoryLabel(category)}
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

              {/* Purpose - Multi-select */}
              <Popover open={purposeOpen} onOpenChange={setPurposeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={purposeOpen}
                    className="w-[160px] justify-between"
                  >
                    {selectedPurposes.length === 0
                      ? "All Purpose"
                      : selectedPurposes.length === 1
                        ? selectedPurposes[0].charAt(0).toUpperCase() + selectedPurposes[0].slice(1)
                        : `${selectedPurposes.length} purposes`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[160px] p-1" align="start">
                  <div className="max-h-[300px] overflow-auto space-y-0.5">
                    {selectedPurposes.length > 0 && (
                      <button
                        onClick={() => setSelectedPurposes([])}
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent"
                      >
                        <X className="h-4 w-4" />
                        Clear selection
                      </button>
                    )}
                    {filterOptions.purposes.map((purpose) => (
                      <label
                        key={purpose}
                        className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedPurposes.includes(purpose)}
                          onCheckedChange={() => setSelectedPurposes(toggleFilter(selectedPurposes, purpose))}
                        />
                        {purpose.charAt(0).toUpperCase() + purpose.slice(1)}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              <Popover open={enabledOpen} onOpenChange={setEnabledOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={enabledOpen}
                    className="w-[140px] justify-between"
                  >
                    {enabledFilter === "all" ? "All Status" : enabledFilter === "true" ? "Enabled" : "Disabled"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[140px] p-0">
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        <CommandItem
                          value="all"
                          onSelect={() => {
                            setEnabledFilter("all");
                            setEnabledOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              enabledFilter === "all" ? "opacity-100" : "opacity-0"
                            )}
                          />
                          All Status
                        </CommandItem>
                        <CommandItem
                          value="true"
                          onSelect={() => {
                            setEnabledFilter("true");
                            setEnabledOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              enabledFilter === "true" ? "opacity-100" : "opacity-0"
                            )}
                          />
                          Enabled
                        </CommandItem>
                        <CommandItem
                          value="false"
                          onSelect={() => {
                            setEnabledFilter("false");
                            setEnabledOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              enabledFilter === "false" ? "opacity-100" : "opacity-0"
                            )}
                          />
                          Disabled
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

            </div>

            <div className="flex gap-2 items-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSearchInput("");
                  setSelectedProviders([]);
                  setSelectedCategories([]);
                  setSelectedNodeTypes([]);
                  setSelectedModelFamilies([]);
                  setSelectedPurposes([]);
                  setEnabledFilter("all");
                }}
                className="h-9 w-9"
                title="Clear all filters"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {selectedModels.size > 0 && (
          <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
            <span className="text-sm font-medium">{selectedModels.size} selected</span>
            <Button size="sm" variant="outline" disabled={bulkUpdating} onClick={() => bulkUpdate({ enabled: true })}>
              Enable All
            </Button>
            <Button size="sm" variant="outline" disabled={bulkUpdating} onClick={() => bulkUpdate({ enabled: false })}>
              Disable All
            </Button>
            <Button size="sm" variant="ghost" disabled={bulkUpdating} onClick={() => setSelectedModels(new Set())}>
              Clear
            </Button>
          </div>
        )}

        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={models.length > 0 && selectedModels.size === models.length}
                      onCheckedChange={toggleAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead className="w-[150px]">Model Family</TableHead>
                  <TableHead className="w-[300px]">Model Name</TableHead>
                  <TableHead className="w-[150px]">Category</TableHead>
                  <TableHead className="w-[120px]">Node Type</TableHead>
                  <TableHead className="w-[100px]">Purpose</TableHead>
                  <TableHead className="w-[100px]">Featured</TableHead>
                  <TableHead className="w-[100px]">Price</TableHead>
                  <TableHead className="w-[100px]">Enabled</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      Loading models...
                    </TableCell>
                  </TableRow>
                ) : models.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No models found
                    </TableCell>
                  </TableRow>
                ) : (
                  models.map((model) => {
                    const edits = rowEdits[model.id];
                    const hasEdits = !!edits;
                    const isSaving = savingRows.has(model.id);

                    const currentCategory = edits?.providerCategory ?? model.providerCategory;
                    const currentNodeType = edits?.nodeType ?? model.nodeType;
                    const currentPurpose = edits?.purpose ?? model.purpose ?? "";

                    return (
                    <TableRow
                      key={model.id}
                      className={cn("cursor-pointer hover:bg-muted/50", hasEdits && "bg-yellow-50/50 dark:bg-yellow-950/20")}
                      onClick={() => !hasEdits && navigate({ to: `/dashboard/model-management/${model.id}` })}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedModels.has(model.id)}
                          onCheckedChange={() => toggleModel(model.id)}
                          aria-label={`Select ${model.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{model.modelFamily || "-"}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{model.name}</p>
                          {model.description && (
                            <p className="text-muted-foreground text-xs line-clamp-1">
                              {model.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={currentCategory}
                          onValueChange={(value) => updateRowEdit(model.id, "providerCategory", value)}
                        >
                          <SelectTrigger className={cn("h-7 text-xs w-[140px]", edits?.providerCategory && "border-yellow-500")}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {AI_MODEL_CATEGORIES.map((cat) => (
                              <SelectItem key={cat} value={cat} className="text-xs">
                                {getCategoryLabel(cat)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={currentNodeType}
                          onValueChange={(value) => updateRowEdit(model.id, "nodeType", value)}
                        >
                          <SelectTrigger className={cn("h-7 text-xs w-[110px]", edits?.nodeType && "border-yellow-500")}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(AI_NODE_TYPES).map((type) => (
                              <SelectItem key={type} value={type} className="text-xs capitalize">
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={currentPurpose || "__none__"}
                          onValueChange={(value) => updateRowEdit(model.id, "purpose", value === "__none__" ? "" : value)}
                        >
                          <SelectTrigger className={cn("h-7 text-xs w-[100px]", edits?.purpose !== undefined && "border-yellow-500")}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__" className="text-xs text-muted-foreground">
                              —
                            </SelectItem>
                            {Object.values(AI_MODEL_PURPOSE).map((p) => (
                              <SelectItem key={p} value={p} className="text-xs capitalize">
                                {p}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium">{model.featuredScore}</span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {edits?.priceUsd !== undefined ? (
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            className={cn("h-7 w-[80px] text-xs", "border-yellow-500")}
                            value={edits.priceUsd}
                            onChange={(e) => updateRowEdit(model.id, "priceUsd", e.target.value === "" ? "" : parseFloat(e.target.value))}
                          />
                        ) : (
                          <button
                            className="text-sm font-medium hover:underline"
                            onClick={() => updateRowEdit(model.id, "priceUsd", model.priceUsd ?? 0)}
                          >
                            {model.priceUsd != null ? `$${model.priceUsd.toFixed(2)}` : "—"}
                          </button>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={model.enabled}
                          disabled={togglingEnabled.has(model.id)}
                          onCheckedChange={() => toggleEnabled(model.id, model.enabled)}
                        />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {hasEdits && (
                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => confirmRowEdit(model.id)}
                              disabled={isSaving}
                              title="Confirm changes"
                            >
                              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                              onClick={() => discardRowEdit(model.id)}
                              disabled={isSaving}
                              title="Discard changes"
                            >
                              <Undo2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  }))
                }
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select
              value={limit.toString()}
              onValueChange={(value) => {
                setLimit(parseInt(value));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[25, 50, 100, 200].map((n) => (
                  <SelectItem key={n} value={n.toString()}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>

        {totalPages > 1 && (
          <Pagination className="mx-0 w-auto">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage(Math.max(1, page - 1))}
                  className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              {(() => {
                const pages: (number | "ellipsis")[] = [];
                if (totalPages <= 7) {
                  // Show all pages if 7 or fewer
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  // Always show first page
                  pages.push(1);
                  // Calculate window around current page
                  const windowStart = Math.max(2, page - 2);
                  const windowEnd = Math.min(totalPages - 1, page + 2);
                  // Ellipsis before window
                  if (windowStart > 2) pages.push("ellipsis");
                  // Pages in window
                  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
                  // Ellipsis after window
                  if (windowEnd < totalPages - 1) pages.push("ellipsis");
                  // Always show last page
                  pages.push(totalPages);
                }
                return pages.map((item, idx) =>
                  item === "ellipsis" ? (
                    <PaginationItem key={`ellipsis-${idx}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <PaginationLink
                        onClick={() => setPage(item)}
                        isActive={page === item}
                        className="cursor-pointer"
                      >
                        {item}
                      </PaginationLink>
                    </PaginationItem>
                  )
                );
              })()}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
        </div>
      </main>
    </div>
  );
}
